<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import * as Sentry from '@sentry/vue'
import { userModule } from '@/store/user/module'
import { toastModule } from '@/store/toast/module'
import { instructorModule } from '@/store/instructor/module'
import { getCookie, clearCookie } from '@/utils'
import UIKit from '@pocketprep/ui-kit'
import SSOTransitionModal from '@/components/SSOTransitionModal.vue'
import { examModule } from '@/store/exam/module'
import { groupModule } from '@/store/group/module'
import { organizationModule } from '@/store/organization/module'
import { studentModule } from '@/store/student/module'
import { licenseModule } from '@/store/license/module'
import type { Teach } from '@pocketprep/types'

const {
    SVGDefinitions,
    Toast,
} = UIKit

const route = useRoute()
const router = useRouter()

const isLoading = ref(true)
const showSSOTransitionModal = ref(false)
const ssoOrgName = ref('')

const user = computed(() => {
    return userModule.state.user
})

const toast = computed(() => {
    return toastModule.state.toast
})

const organizations = computed(() => {
    return organizationModule.getters.getOrganizations()
})

const currentUserOrgInstructors = computed(() => {
    return instructorModule.getters.getCurrentUserOrgInstructors()
        .filter(i => !getOrgInstructorOrganization(i)?.archivedAt)
})

const getOrgInstructorOrganization = (orgInstructor: Teach.Class.OrgInstructorJSON) => {
    return organizations.value[orgInstructor.organization.objectId]
}

const currentUserPendingOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentUserPendingOrgInstructor()
})

const messageListener = (event: MessageEvent) => {
    if (event.data === 'clearLocalStorage') {
        localStorage.clear()
    }
}

const closeSSOTransitionModal = () => {
    showSSOTransitionModal.value = false
}

const clearToast = () => {
    toastModule.actions.clearToast()
}

/**
 * Check for any pending OrgInstructor rows. If one exists, the user needs to accept their invitation
 */
const checkForPendingOrgInstructors = () => {
    if (route.name !== 'invite') {
        if (currentUserPendingOrgInstructor.value) {
            router.push({
                name: 'invite',
            })
        }
    }
}

onMounted(async () => {
    // Listen for clearLocalStorage postMessage - for admin iframe support
    try {
        window.addEventListener('message', messageListener)

        const currentUser = user.value

        const sessionCookieName = import.meta.env.VUE_APP_SESSION_COOKIE_NAME
        if (sessionCookieName) {
            const newSessionToken = getCookie(sessionCookieName)

            if (newSessionToken) {
                await userModule.actions.signInWithSession({ sessionToken: newSessionToken })
                const queryParams = new URLSearchParams(window.location.search)

                const hasSeenSSOTransitionModal = userModule.state.user?.webConfig?.hasSeenSSOTransitionModal
                const querySsoOrgName = queryParams.get('ssoOrgName')
                if (!hasSeenSSOTransitionModal && querySsoOrgName) {
                    showSSOTransitionModal.value = true
                    ssoOrgName.value = querySsoOrgName
                    userModule.actions.updateWebConfig({
                        hasSeenWelcomeModal: true,
                        hasSeenSSOTransitionModal: true,
                    })
                }

                clearCookie(sessionCookieName)
                if (route.name !== 'stats') {
                    const redirect = queryParams.get('redirect')
                    if (redirect) {
                        const query =
                            Object.fromEntries(new URLSearchParams(redirect.split('?')[1]))
                        router.push({ path: redirect, query })
                    } else {
                        router.push({ name: 'stats', query: route.query })
                    }
                }
            }
            if (currentUser) {
                Sentry.setUser({
                    id: currentUser.objectId,
                    email: currentUser.email,
                })

                await Promise.all([
                    examModule.actions.fetchCurrentOrgExams(),
                    groupModule.actions.fetchCurrentOrgGroups(),
                    studentModule.actions.fetchCurrentOrgStudents(),
                    licenseModule.actions.fetchCurrentOrgLicenses(),
                    examModule.actions.fetchExamMetadata(),
                    organizationModule.actions.fetchCurrentOrganization(),
                    instructorModule.actions.fetchCurrentUserOrgInstructors(),
                ])

                const currentOrg = organizationModule.getters.getCurrentOrganization()
                const isCurrentOrgArchived = !!currentOrg?.archivedAt
                const unarchivedInstructorOrg = currentUserOrgInstructors
                    .value[0]?.organization as Teach.Class.OrganizationJSON
                if (isCurrentOrgArchived && unarchivedInstructorOrg) {
                    // we never archive the org if it's the only the user has, so we don't need to cover that case,
                    // for now it's okay if user stays within the archived org
                    await userModule.actions.updateCurrentOrganization(unarchivedInstructorOrg)
                }

                organizationModule.actions.validateOrgState()

                checkForPendingOrgInstructors()
            }
        }
    } catch (e) {
        // Rethrow and log sentry
        Sentry.captureException(e)
        throw e
    } finally {
        // Always set isLoading to false so we don't show a blank screen
        isLoading.value = false
    }
})

onBeforeUnmount(() => {
    window.removeEventListener('message', messageListener)
})

watch(currentUserPendingOrgInstructor, checkForPendingOrgInstructors)
</script>

<template>
    <div class="teach">
        <SVGDefinitions />
        <PortalTarget name="modal" multiple />
        <PortalTarget name="sidepanel" multiple />
        <SSOTransitionModal
            v-if="showSSOTransitionModal && !isLoading"
            :orgName="ssoOrgName"
            @close="closeSSOTransitionModal"
        />
        <template v-if="!isLoading">
            <RouterView />
        </template>
        <Toast
            v-if="toast"
            @close="clearToast"
        >
            {{ toast.title }}
            <template v-if="toast.subtext" #subtext>
                {{ toast.subtext }}
            </template>
        </Toast>
    </div>
</template>

<style lang="scss">

body,
html {
    height: 100%;
    width: 100%;
    overflow: hidden;

    .teach {
        height: 100%;
    }
}

// this disables scrolling on the study__body class when full-screen divs are open which helps prevent double
// scrolling on mobile devices (and probably other devices as well)
body.uikit-modal-open .skeleton-body,
body.uikit-sidepanel-open .skeleton-body {
    overflow: hidden !important;
}

// temporary side panel styling override until design reviews and updates
.uikit-side-panel__head,
.uikit-side-panel__foot {
    background-color: $barely-background;
}

.uikit-side-panel__content {
    background: $white;
}
</style>
